<script setup lang="ts">
  import { computed } from 'vue';

  import type { User } from '@stuut/api/models';

  import { UserAvatar } from '@/components/users';
  import { useProfile } from '@/store';

  interface Props {
    assignee?: User | null;
    isOooCovering?: boolean;
    showAvatarOnly?: boolean;
    size?: 'md' | 'sm' | 'xs';
  }

  const props = withDefaults(defineProps<Props>(), { size: 'sm' });

  const profileStore = useProfile();

  const oooTooltip = computed(
    () => `${props.assignee?.name ?? ''} is out of office — you're covering`,
  );
</script>

<template>
  <div :class="{ 'w-[150px] text-left': !showAvatarOnly }" class="text-xs">
    <div
      v-if="assignee?.id"
      class="flex flex-nowrap space-x-2 items-center truncate"
      :class="isOooCovering && 'overflow-visible!'"
    >
      <div v-if="isOooCovering" :class="showAvatarOnly ? 'relative' : 'flex items-center'">
        <UserAvatar
          :user="profileStore.profile"
          :size="size"
          tooltip-text="Assigned as a backup"
          class="relative z-10"
        />
        <UserAvatar
          :user="assignee"
          :size="size"
          :tooltip-text="oooTooltip"
          :class="
            showAvatarOnly
              ? 'absolute top-0 left-full -translate-x-1.5 opacity-60 grayscale'
              : 'opacity-60 grayscale -ml-1.5'
          "
        />
      </div>
      <UserAvatar v-else :user="assignee" :size="size" />

      <p v-if="!showAvatarOnly" class="truncate">{{ assignee?.name }}</p>
    </div>

    <template v-else>
      <div
        v-if="showAvatarOnly"
        class="rounded-full border-border border text-muted-foreground flex items-center justify-center"
        :class="{
          'w-6 h-6': !size || size === 'sm',
          'w-8 h-8': size === 'md',
          'w-5 h-5': size === 'xs',
        }"
      >
        <i
          class="fas fa-user leading-none flex-shrink-1"
          :class="{
            '!text-sm w-[10px]': !size || size === 'md',
            '!text-3xs w-[7px]': !size || size === 'sm',
            '!text-4xs w-[6.5px]': size === 'xs',
          }"
        ></i>
      </div>

      <div v-else>No Assignee</div>
    </template>
  </div>
</template>

<style scoped></style>
